import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, RefreshCw, AlertTriangle } from 'lucide-react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { useTheme } from './ThemeContext';

export default function VisualScheduler({
  companyId,
  selectedDate,
  selectedTime,
  onSelectSlot,
  estimatedDuration = 60,
  onDurationChange = null, // Callback when duration is changed in the scheduler
  isRecurring = false,
  recurrenceRule = 'weekly',
  recurrenceEndDate = '',
  onConflictsFound = null,
  compact = false,
  slotMinutes = 60, // 15, 30, or 60
  showDurationSelector = true // Set to false if parent already shows duration controls
}) {
  const { colors } = useTheme();
  const [existingJobs, setExistingJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [conflicts, setConflicts] = useState([]);
  const [localDuration, setLocalDuration] = useState(estimatedDuration);

  // Sync local duration with prop if it changes externally
  useEffect(() => {
    setLocalDuration(estimatedDuration);
  }, [estimatedDuration]);

  const handleDurationChange = (newDuration) => {
    setLocalDuration(newDuration);
    if (onDurationChange) {
      onDurationChange(newDuration);
    }
  };

  // Week navigation
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(today);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  });

  // Generate time slots based on slotMinutes setting (24 hours)
  const timeSlots = [];
  const startHour = 0;
  const endHour = 23;
  for (let hour = startHour; hour <= endHour; hour++) {
    for (let min = 0; min < 60; min += slotMinutes) {
      timeSlots.push(`${hour}:${min.toString().padStart(2, '0')}`);
    }
  }

  // Get week dates
  const getWeekDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const allWeekDates = getWeekDates();

  // On mobile, filter out past days to save space
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter to only show today and future days on mobile
  const weekDates = isMobile
    ? allWeekDates.filter(date => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date >= today;
      })
    : allWeekDates;

  // Ensure we have at least 3 days to show (for layout purposes)
  const displayDates = weekDates.length >= 1 ? weekDates : allWeekDates.slice(-3);

  const formatDateISO = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Load existing jobs for the week
  useEffect(() => {
    const loadJobs = async () => {
      if (!companyId) {
        setLoadingJobs(false);
        return;
      }

      setLoadingJobs(true);
      try {
        const startDate = formatDateISO(weekDates[0]);
        const endDate = formatDateISO(weekDates[6]);

        const { data, error } = await supabaseData
          .from('jobs')
          .select('*, customers(name)')
          .eq('company_id', companyId)
          .gte('scheduled_date', startDate)
          .lte('scheduled_date', endDate)
          .not('status', 'eq', 'cancelled');

        if (!error && data) {
          setExistingJobs(data);
        }
      } catch (err) {
        // Handle timeout or network errors - just show empty schedule
        console.warn('Failed to load jobs for scheduler:', err.message);
        setExistingJobs([]);
      } finally {
        setLoadingJobs(false);
      }
    };

    loadJobs();
  }, [weekStart, companyId]);

  // Check for recurring conflicts
  useEffect(() => {
    if (!isRecurring || !selectedDate || !recurrenceEndDate || !selectedTime) {
      setConflicts([]);
      if (onConflictsFound) onConflictsFound([]);
      return;
    }

    const checkConflicts = async () => {
      const occurrences = generateOccurrences(selectedDate, recurrenceEndDate, recurrenceRule);

      const { data: futureJobs } = await supabaseData
        .from('jobs')
        .select('scheduled_date, scheduled_time, customers(name)')
        .eq('company_id', companyId)
        .gte('scheduled_date', selectedDate)
        .lte('scheduled_date', recurrenceEndDate)
        .not('status', 'eq', 'cancelled');

      if (!futureJobs) return;

      const foundConflicts = [];
      occurrences.forEach(occDate => {
        futureJobs.forEach(job => {
          if (job.scheduled_date === occDate && job.scheduled_time === selectedTime) {
            foundConflicts.push({
              date: occDate,
              time: job.scheduled_time,
              customerName: job.customers?.name || 'Unknown'
            });
          }
        });
      });

      setConflicts(foundConflicts);
      if (onConflictsFound) onConflictsFound(foundConflicts);
    };

    checkConflicts();
  }, [isRecurring, selectedDate, selectedTime, recurrenceEndDate, recurrenceRule, companyId]);

  const generateOccurrences = (startDate, endDate, rule) => {
    const occurrences = [];
    // Parse dates with noon time to avoid timezone issues
    const start = new Date(startDate + 'T12:00:00');
    const end = new Date(endDate + 'T12:00:00');
    let current = new Date(start);

    while (current <= end) {
      occurrences.push(formatDateISO(current));
      switch (rule) {
        case 'weekly':
          current.setDate(current.getDate() + 7);
          break;
        case 'biweekly':
          current.setDate(current.getDate() + 14);
          break;
        case 'monthly':
          current.setMonth(current.getMonth() + 1);
          break;
        default:
          current.setDate(current.getDate() + 7);
      }
    }
    return occurrences;
  };

  // Convert time string "HH:MM" to minutes since midnight
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + (m || 0);
  };

  // Get jobs for a specific date and time slot
  const getJobsAt = (date, time) => {
    const dateStr = formatDateISO(date);
    const slotStart = timeToMinutes(time);
    const slotEnd = slotStart + slotMinutes;

    return existingJobs.filter(job => {
      if (job.scheduled_date !== dateStr) return false;
      if (!job.scheduled_time) return false;

      const jobStart = timeToMinutes(job.scheduled_time);
      const jobDuration = job.estimated_duration || 60;
      const jobEnd = jobStart + jobDuration;

      // Check if there's any overlap between slot and job
      return slotStart < jobEnd && slotEnd > jobStart;
    });
  };

  // Check if this slot is the start of a job (for displaying the label)
  const isJobStart = (date, time) => {
    const dateStr = formatDateISO(date);
    const slotStart = timeToMinutes(time);

    return existingJobs.find(job => {
      if (job.scheduled_date !== dateStr) return false;
      if (!job.scheduled_time) return false;

      const jobStart = timeToMinutes(job.scheduled_time);
      // Job starts within this slot
      return jobStart >= slotStart && jobStart < slotStart + slotMinutes;
    });
  };

  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isRecurringOccurrence = (date) => {
    if (!isRecurring || !selectedDate || !recurrenceEndDate) return false;
    const dateStr = formatDateISO(date);
    const occurrences = generateOccurrences(selectedDate, recurrenceEndDate, recurrenceRule);
    return occurrences.includes(dateStr) && dateStr !== selectedDate;
  };

  const prevWeek = () => {
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() - 7);
    setWeekStart(newStart);
  };

  const nextWeek = () => {
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() + 7);
    setWeekStart(newStart);
  };

  const goToToday = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(today);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0);
    setWeekStart(monday);
  };

  const handleSlotClick = (date, time) => {
    if (isPastDate(date)) return;
    const jobsAtSlot = getJobsAt(date, time);
    if (jobsAtSlot.length > 0) return;

    onSelectSlot(formatDateISO(date), time);
  };

  const formatTimeDisplay = (time) => {
    const [hourStr, minStr] = time.split(':');
    const hour = parseInt(hourStr);
    const minutes = minStr || '00';
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    // Show minutes if not :00, or always in compact mode for clarity
    const showMinutes = minutes !== '00' || compact;
    return showMinutes
      ? `${hour12}:${minutes}${compact ? '' : ' '}${ampm}`
      : `${hour12}${compact ? '' : ' '}${ampm}`;
  };

  const isSelected = (date, time) => {
    return formatDateISO(date) === selectedDate && time === selectedTime;
  };

  const isToday = (date) => {
    const today = new Date();
    return formatDateISO(date) === formatDateISO(today);
  };

  return (
    <div>
      {/* Week Navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0.75rem',
        padding: compact ? '0.5rem' : '0.75rem',
        background: colors.bgInput,
        borderRadius: '8px'
      }}>
        <button
          onClick={prevWeek}
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: compact ? '0.35rem 0.5rem' : '0.5rem 0.75rem',
            color: colors.text,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            fontSize: compact ? '0.8rem' : '0.9rem'
          }}
        >
          <ChevronLeft size={compact ? 14 : 18} />
          {!compact && 'Prev'}
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ fontWeight: 'bold', color: colors.text, fontSize: compact ? '0.85rem' : '1rem' }}>
            {weekDates[0].toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
          </span>
          <button
            onClick={goToToday}
            style={{
              background: colors.accent,
              border: 'none',
              borderRadius: '6px',
              padding: compact ? '0.25rem 0.5rem' : '0.4rem 0.75rem',
              color: '#fff',
              cursor: 'pointer',
              fontSize: compact ? '0.75rem' : '0.85rem',
              fontWeight: 'bold'
            }}
          >
            Today
          </button>
        </div>

        <button
          onClick={nextWeek}
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: compact ? '0.35rem 0.5rem' : '0.5rem 0.75rem',
            color: colors.text,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            fontSize: compact ? '0.8rem' : '0.9rem'
          }}
        >
          {!compact && 'Next'}
          <ChevronRight size={compact ? 14 : 18} />
        </button>
      </div>

      {/* Duration Selector - only show if parent doesn't have one */}
      {showDurationSelector && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '0.75rem',
          padding: compact ? '0.5rem 0.75rem' : '0.6rem 1rem',
          background: colors.bgInput,
          borderRadius: '8px',
          flexWrap: 'wrap'
        }}>
          <span style={{
            color: colors.textMuted,
            fontSize: compact ? '0.8rem' : '0.9rem',
            fontWeight: '500'
          }}>
            Job Duration:
          </span>
          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
            {[30, 60, 90, 120, 180, 240].map(mins => (
              <button
                key={mins}
                onClick={() => handleDurationChange(mins)}
                style={{
                  padding: compact ? '0.3rem 0.5rem' : '0.4rem 0.7rem',
                  background: localDuration === mins ? colors.accent : colors.bgCard,
                  color: localDuration === mins ? '#fff' : colors.text,
                  border: `1px solid ${localDuration === mins ? colors.accent : colors.border}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: compact ? '0.75rem' : '0.85rem',
                  fontWeight: localDuration === mins ? '600' : '400',
                  whiteSpace: 'nowrap'
                }}
              >
                {mins < 60 ? `${mins}m` : mins === 60 ? '1hr' : mins === 90 ? '1.5hr' : `${mins / 60}hr`}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Calendar Grid */}
      {loadingJobs ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: colors.textMuted }}>
          Loading schedule...
        </div>
      ) : (
        <div style={{
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: isMobile ? '60vh' : '70vh',
          WebkitOverflowScrolling: 'touch',
          position: 'relative'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `${compact ? '45px' : '55px'} repeat(${displayDates.length}, 1fr)`,
            gap: '1px',
            background: colors.border,
            borderRadius: '8px',
            overflow: 'hidden',
            minWidth: isMobile ? 'auto' : (compact ? '500px' : '650px')
          }}>
            {/* Header Row - Sticky */}
            <div style={{
              background: colors.bgInput,
              padding: compact ? '0.35rem' : '0.5rem',
              position: 'sticky',
              top: 0,
              left: 0,
              zIndex: 3
            }}></div>
            {displayDates.map((date, idx) => (
              <div
                key={idx}
                style={{
                  background: isToday(date) ? colors.accent : colors.bgInput,
                  padding: compact ? '0.4rem 0.25rem' : '0.6rem 0.5rem',
                  textAlign: 'center',
                  color: isToday(date) ? '#fff' : colors.text,
                  fontWeight: isToday(date) ? 'bold' : 'normal',
                  position: 'sticky',
                  top: 0,
                  zIndex: 2
                }}
              >
                <div style={{ fontSize: compact ? '0.65rem' : '0.75rem', opacity: 0.8 }}>
                  {date.toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
                <div style={{ fontSize: compact ? '0.9rem' : '1.1rem', fontWeight: 'bold' }}>
                  {date.getDate()}
                </div>
              </div>
            ))}

            {/* Time Rows */}
            {timeSlots.map((time) => (
              <>
                <div
                  key={`label-${time}`}
                  style={{
                    background: colors.bgCard,
                    padding: compact ? '0.25rem' : '0.5rem',
                    fontSize: compact ? '0.65rem' : '0.75rem',
                    color: colors.textMuted,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    position: 'sticky',
                    left: 0,
                    zIndex: 1
                  }}
                >
                  {formatTimeDisplay(time)}
                </div>

                {displayDates.map((date, dayIdx) => {
                  const jobsAtSlot = getJobsAt(date, time);
                  const jobStart = isJobStart(date, time);
                  const isPast = isPastDate(date);
                  const selected = isSelected(date, time);
                  const hasJob = jobsAtSlot.length > 0;
                  const isGhostOccurrence = isRecurringOccurrence(date) && time === selectedTime;

                  return (
                    <div
                      key={`${time}-${dayIdx}`}
                      onClick={() => handleSlotClick(date, time)}
                      style={{
                        background: selected
                          ? colors.accent
                          : isGhostOccurrence
                          ? '#9b59b640'
                          : hasJob
                          ? `${colors.info}40`
                          : isPast
                          ? colors.bgInput
                          : colors.bgCard,
                        padding: '0.2rem',
                        minHeight: compact ? '35px' : '42px',
                        cursor: isPast || hasJob ? 'not-allowed' : 'pointer',
                        position: 'relative',
                        opacity: isPast ? 0.5 : 1,
                        transition: 'background 0.15s',
                        border: selected ? `2px solid ${colors.accent}` : isGhostOccurrence ? '2px dashed #9b59b6' : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (!isPast && !hasJob && !selected && !isGhostOccurrence) {
                          e.currentTarget.style.background = `${colors.accent}30`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isPast && !hasJob && !selected && !isGhostOccurrence) {
                          e.currentTarget.style.background = colors.bgCard;
                        }
                      }}
                    >
                      {jobStart && (
                        <div style={{
                          background: colors.info,
                          color: '#fff',
                          padding: '0.15rem 0.3rem',
                          borderRadius: '3px',
                          fontSize: compact ? '0.6rem' : '0.7rem',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {jobStart.customers?.name || 'Job'}
                        </div>
                      )}
                      {isGhostOccurrence && !hasJob && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)'
                        }}>
                          <RefreshCw size={compact ? 12 : 14} color="#9b59b6" />
                        </div>
                      )}
                      {selected && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: compact ? '0.6rem' : '0.7rem'
                        }}>
                          ✓
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            ))}
          </div>
        </div>
      )}

      {/* Conflict Warnings */}
      {conflicts.length > 0 && (
        <div style={{
          marginTop: '0.75rem',
          padding: '0.75rem',
          background: `${colors.error}20`,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.5rem',
          border: `1px solid ${colors.error}`
        }}>
          <AlertTriangle size={18} color={colors.error} style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <div style={{ color: colors.error, fontWeight: 'bold', marginBottom: '0.25rem', fontSize: compact ? '0.85rem' : '0.9rem' }}>
              {conflicts.length} Conflict(s) with Future Occurrences
            </div>
            <div style={{ color: colors.text, fontSize: compact ? '0.75rem' : '0.85rem' }}>
              {conflicts.slice(0, 2).map((c, i) => (
                <div key={i}>
                  {new Date(c.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {c.customerName}
                </div>
              ))}
              {conflicts.length > 2 && (
                <div style={{ color: colors.textMuted }}>
                  ...and {conflicts.length - 2} more
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginTop: '0.75rem',
        fontSize: compact ? '0.7rem' : '0.8rem',
        color: colors.textMuted,
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <div style={{ width: '12px', height: '12px', background: `${colors.info}40`, borderRadius: '2px' }}></div>
          <span>Booked</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <div style={{ width: '12px', height: '12px', background: colors.accent, borderRadius: '2px' }}></div>
          <span>Selected</span>
        </div>
        {isRecurring && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <div style={{ width: '12px', height: '12px', background: '#9b59b640', border: '1px dashed #9b59b6', borderRadius: '2px' }}></div>
            <span>Recurring</span>
          </div>
        )}
      </div>
    </div>
  );
}
